export const BRIEFING_STORAGE_KEY = 'wingit-flight-briefings-v1';

export const briefingSteps = [
  { id: 'details', label: 'Flight Details' },
  { id: 'route', label: 'Route' },
  { id: 'briefing', label: 'Briefing' },
  { id: 'attachments', label: 'Attachments' },
  { id: 'confirm', label: 'Confirm' },
];

export const briefingChecklist = [
  { id: 'natsBrief', label: 'Official NATS AIS Narrow Route Brief generated' },
  { id: 'depNotam', label: 'Departure aerodrome NOTAMs reviewed' },
  { id: 'destNotam', label: 'Destination aerodrome NOTAMs reviewed' },
  { id: 'altNotam', label: 'Alternate aerodrome NOTAMs reviewed' },
  { id: 'enrouteNotam', label: 'En-route NOTAMs reviewed' },
  { id: 'rt', label: 'RA(T) / Temporary restrictions checked' },
  { id: 'danger', label: 'Danger areas checked' },
  { id: 'activity', label: 'Parachuting / gliding / winch activity checked' },
  { id: 'weather', label: 'Weather reviewed' },
  { id: 'fuel', label: 'Fuel/endurance checked' },
  { id: 'massBalance', label: 'Mass and balance checked' },
  { id: 'ppr', label: 'PPR / landing permission confirmed' },
];

export const briefingStatusMeta = {
  red: { label: 'RED', fill: '#ff6e6e', border: '#ff9393', background: 'rgba(255, 110, 110, 0.12)' },
  amber: { label: 'AMBER', fill: '#ffcc66', border: '#ffd98c', background: 'rgba(255, 204, 102, 0.12)' },
  green: { label: 'GREEN', fill: '#7df0a8', border: '#9af5bb', background: 'rgba(125, 240, 168, 0.12)' },
};

function safeParse(jsonText, fallback) {
  try {
    return JSON.parse(jsonText);
  } catch (_error) {
    return fallback;
  }
}

export function loadBriefingStore() {
  if (typeof window === 'undefined') return {};
  const raw = window.localStorage.getItem(BRIEFING_STORAGE_KEY);
  const store = safeParse(raw || '{}', {});
  return store && typeof store === 'object' ? store : {};
}

export function saveBriefingStore(store) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(BRIEFING_STORAGE_KEY, JSON.stringify(store || {}));
  window.dispatchEvent(new Event('wingit-briefing-store-updated'));
}

export function makeBriefingCompositeKey({ userId, aircraftId, date, time }) {
  return `draft:${userId || 'unknown'}:${aircraftId || 'unknown'}:${date || 'unknown'}:${time || 'unknown'}`;
}

export function createEmptyBriefingDraft(overrides = {}) {
  return {
    pilotName: '',
    flightDate: '',
    etd: '',
    departure: '',
    destination: '',
    alternate: '',
    flightRules: 'VFR',
    plannedLevel: '',
    routeWidth: '20 NM',
    routePoints: '',
    routeDistanceNm: '',
    routeEte: '',
    routePlannedDeparture: '',
    routePlannedArrival: '',
    importSource: '',
    importUpdatedAt: '',
    notamDetected: false,
    weatherDetected: false,
    notes: '',
    officialBriefingConfirmed: false,
    declarationConfirmed: false,
    checklist: briefingChecklist.reduce((accumulator, item) => {
      accumulator[item.id] = false;
      return accumulator;
    }, {}),
    attachments: {
      pib: '',
      weather: '',
      plog: '',
    },
    bookingIds: [],
    status: 'red',
    updatedAt: null,
    ...overrides,
  };
}

export function getBriefingRecordForBooking(booking, userId) {
  const store = loadBriefingStore();
  const bookingKey = booking?.id ? `booking:${booking.id}` : '';
  const compositeKey = makeBriefingCompositeKey({
    userId,
    aircraftId: booking?.aircraft_id,
    date: booking?.date,
    time: booking?.time,
  });
  return (bookingKey && store[bookingKey]) || store[compositeKey] || null;
}

export function getBriefingStatus(record) {
  if (!record) return 'red';
  if (record.status === 'green') return 'green';
  if (record.status === 'amber') return 'amber';
  return 'red';
}

export function persistBriefingRecord({ compositeKey, bookingIds = [], draft, status }) {
  const store = loadBriefingStore();
  const now = new Date().toISOString();
  const record = {
    ...createEmptyBriefingDraft(draft),
    ...draft,
    bookingIds,
    status: status || 'amber',
    updatedAt: now,
  };

  if (compositeKey) {
    store[compositeKey] = record;
  }

  bookingIds.forEach((bookingId) => {
    if (bookingId !== undefined && bookingId !== null && bookingId !== '') {
      store[`booking:${bookingId}`] = record;
    }
  });

  saveBriefingStore(store);
  return record;
}

export function countCompletedChecklistItems(checklist = {}) {
  return briefingChecklist.reduce((count, item) => count + (checklist[item.id] ? 1 : 0), 0);
}

export function deriveBriefingStatus(draft) {
  if (!draft) return 'red';
  const completedItems = countCompletedChecklistItems(draft.checklist || {});
  const hasCoreDetails = Boolean(
    draft.pilotName &&
    draft.flightDate &&
    draft.departure &&
    draft.destination &&
    draft.etd &&
    draft.routePoints &&
    draft.officialBriefingConfirmed
  );
  const hasDeclaration = Boolean(draft.declarationConfirmed);
  if (hasCoreDetails && hasDeclaration && completedItems === briefingChecklist.length) return 'green';
  if (hasCoreDetails || completedItems > 0) return 'amber';
  return 'red';
}